<template>
  <Modal cClass="unpublish__modal" hClass="bg-light1 p-0 unpublish__header" mClass="unpublish__body" fClass="p-0" @cancel="cancel">
    <template #header>
      <div class="unpublish__header d-flex align-items-center justify-content-between px-2">
        <p class="heading-16-medium text-light-high clickable">{{ modalTitle }}</p>
        <div class="btn-icon clickable" @click="cancel">
          <i class="icon icon-cancel text-light-high"></i>
        </div>
      </div>
    </template>
    <template #default>
      <div class="unpublish__search">
        <Noti v-if="displayMessageUnpublishPage && pageType == 'productpages'" class="mb-2" type="infor" isInlineAction isCloseOnAction :actionTitle="'Got it'" :htmlDesc="$t('downgrade.unPublish.modal.message', { title: defaultPageTitle })" @action="gotItUnpublishPage"> </Noti>
        <GpInputSearch :cClass="'gp-new__search-item'" :placeholder="$tc('group.inputSearchPlaceholder')" :indicating="indicatingSearchPage" v-model="queryPage" @onChange="queryPages" clear> </GpInputSearch>
      </div>
      <div v-if="loadingIndicator" class="d-flex align-items-center justify-content-center unpublish__loader">
        <GLoading size="large" />
      </div>
      <div v-else-if="isNoItem">
        <SVGNoItem :message="'No pages match your search.'" />
      </div>
      <div class="px-2" v-else>
        <div class="d-flex align-items-center mb-2" :class="isModalDesc ? 'justify-content-between' : 'justify-content-end'">
          <span class="paragraph-14-16-medium" v-html="modalDesc" v-if="isModalDesc"></span>
          <div class="d-flex align-items-center" v-if="sortedPages.length">
            <span class="text-dark6 paragraph-12-regular mr-1">{{ selectLabel }}</span>
            <label class="unpublish__select-all-checkbox unpublish-card__checkbox-item" :class="[{ checked: (isSelectAll() && !listOnlyContainsDefault) || selectedPage > 0 }, { 'unchecked-disabled': isSubmitting || listOnlyContainsDefault }]">
              <i class="icon text-light1" :class="isSelectAll() && !listOnlyContainsDefault ? 'icon-selected' : selectedPage > 0 && 'icon-indeterminate'" />
              <input type="checkbox" @click="selectAll()" />
            </label>
          </div>
        </div>
        <div class="unpublish__page-list" v-if="sortedPages.length">
          <TemplateCard v-for="(page, index) in sortedPages" :key="page.id + '_' + index" :page="page" :currentPageType="pageType" />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="unpublish__footer d-flex align-items-center justify-content-end">
        <button class="btn btn-danger2 unpublish__footer_btn" :class="{ disabled: selectedPage == 0 }" @click="unpublish">
          <div class="opacity-0">{{ actionText }}</div>
          <div class="unpublish__footer_btn__number">
            <div class="d-flex">
              <div>{{ actionText }}</div>
              <div class="gfLoader-3Dots" style="margin:0;font-size:14px" v-if="isSubmitting">
                <span class="gfLoader-status status"></span>
                <span class="gfLoader-3Dots-dot1">.</span>
                <span class="gfLoader-3Dots-dot2">.</span>
                <span class="gfLoader-3Dots-dot3">.</span>
              </div>
            </div>
          </div>
          <div class="unpublish__footer_btn__progress-bar"></div>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script>
import TemplateCard from "./components/TemplateCard.vue";
import { GpInputSearch } from "@/core/shared/GpForms";
import SVGNoItem from "@/core/shared/SVGNoItem.vue";
import Noti from "@/core/shared/base/Notification";
import { mapActions, mapState, mapMutations, mapGetters } from "vuex";
import trackEvent, { PRICING_MODEL_EVENT } from "@/utils/tracker";
import errorHandle from "@/mixins/errorHandle";

export default {
  name: "UnpublishModal",
  components: {
    TemplateCard,
    GpInputSearch,
    SVGNoItem,
    Noti,
  },
  mixins: [errorHandle()],
  data() {
    return {
      indicatingSearchPage: false,
      runnerSearch: null,
      loadingIndicator: false,
      isSubmitting: false,
      queryPage: ""
    };
  },
  computed: {
    ...mapState("downgrade", {
      pages: state => state.pages,
      pageType: state => state.unpublishPageType,
      pagesPublishCount: state => state.pagesPublishCount
    }),
    ...mapGetters("downgrade", {
      sortedArticlePages: "sortedArticlePages",
      sortedProductPages: "sortedProductPages",
      mergedUnpublishedList: "mergedUnpublishedList"
    }),
    ...mapState("account", {
      shopInfo: "shopInfo"
    }),
    unpublishList() {
      return this.mergedUnpublishedList(this.pageType);
    },
    sortedPages() {
      switch (this.pageType) {
        case "articlepages":
          return this.sortedArticlePages;
        default:
          return this.sortedProductPages;
      }
    },
    listPages() {
      return this.pages[this.pageType].list;
    },
    isNoItem() {
      return !this.sortedPages || this.sortedPages.length == 0;
    },
    defaultPageTitle() {
      for (let i = 0; i < this.sortedPages.length; i++) {
        const page = this.sortedPages[i];
        if (page.published) return page.title;
      }
      return `${this.$tc("generals.default")} ${this.$tc("page.page", 1)}`.toLowerCase();
    },
    pageTypeText() {
      return {
        articlepages: this.$tc("dataPlan.plan.options.blog_posts").toLowerCase(),
        productpages: this.$tc("dataPlan.plan.options.product_pages").toLowerCase()
      };
    },
    modalTitle() {
      return this.$t("downgrade.unPublish.modal.title", { pageType: this.pageTypeText[this.pageType] });
    },
    modalDesc() {
      const productPageText = `${this.$tc("page.productLower", 1)} ${this.$tc("generals.templateLower", this.needUnpublishPageCount == 1 ? 1 : 2)}`;
      const articlePageText = `${this.needUnpublishPageCount == 1 ? this.$tc("dashboard.articlePage") : this.$tc("page.blog")}`.toLowerCase();
      const pageTypeText = {
        articlepages: articlePageText,
        productpages: productPageText
      };
      return `${this.$t("downgrade.unPublish.modal.desc", {
        pageCount: `
          <span class='unpublish__page-count'>
            ${this.needUnpublishPageCount}
          </span>`
      })} ${pageTypeText[this.pageType]}`;
    },
    actionText() {
      if (this.isSubmitting) return `${this.$tc("downgrade.unPublish.modal.btnText.submit")}`;
      const unpublishNumber = this.$t("downgrade.unPublish.modal.btnText", { pageCount: this.selectedPage });
      const ofPage = this.$tc("page.page", this.selectedPage == 1 ? 1 : 2).toLowerCase();
      return `${unpublishNumber} ${ofPage}`;
    },
    selectedPage() {
      return this.unpublishList.length;
    },

    publishedProductCount() {
      const { isGemPagesV6, isGemPagesV7, isGemPagesMixed } = this.shopVersion;

      if (isGemPagesV6) return this.shopInfo.publishedProductPagesCount;
      if (isGemPagesV7) return this.pagesPublishCount.publishedProductPagesCount;
      if (isGemPagesMixed) return this.pagesPublishCount.publishedProductPagesCount + this.shopInfo.publishedProductPagesCount;

      return 0;
    },
    publishedArticleCount() {
      const { isGemPagesV6, isGemPagesV7, isGemPagesMixed } = this.shopVersion;

      if (isGemPagesV6) return this.shopInfo.publishedArticlePagesCount;
      if (isGemPagesV7) return this.pagesPublishCount.publishedArticlePagesCount;
      if (isGemPagesMixed) return this.pagesPublishCount.publishedArticlePagesCount + this.shopInfo.publishedArticlePagesCount;

      return 0;
    },
    filteredPages() {
      const filteredPages = this.listPages.filter(page => {
        return !this.isDefault(page);
      });
      return filteredPages;
    },
    listOnlyContainsDefault() {
      return this.listPages.every(page => this.isDefault(page));
    },
    pageLimit() {
      return this.pages[this.pageType].limit;
    },
    needUnpublishPageCount() {
      let currentPublishedPage = this.listPages.length;
      if(this.pageType === "productpages") {
        currentPublishedPage = this.publishedProductCount
      } else if(this.pageType === "articlepages") {
        currentPublishedPage = this.publishedArticleCount
      }
     
      const limit = this.pageLimit;
      return `${currentPublishedPage - limit}`;
    },
    isModalDesc() {
      let currentPublishedPage = this.listPages.length;
      if(this.pageType === "productpages") {
        currentPublishedPage = this.publishedProductCount
      } else if(this.pageType === "articlepages") {
        currentPublishedPage = this.publishedArticleCount
      }
      return currentPublishedPage > this.pageLimit;
    },
    selectLabel() {
      if (this.selectedPage > 0 && !this.isSelectAll()) {
        const count = `${this.selectedPage}/${this.listPages.length}`;
        return this.$t("generals.selectPartially", { count: count });
      } else {
        return this.$tc("generals.selectALL");
      }
    },
    displayMessageUnpublishPage() {
      const gotMessage = localStorage.getItem("gotUnpublishMessageList");
      return gotMessage ? false : true;
    },
    currentVersion() {
      return (this && this.shopInfo && this.shopInfo.isGemPagesV7) || window.___gemShopInfo.isGemPagesV7 || false;
    },
    shopVersion() {
      return this && this.shopInfo && this.shopInfo.shopType || window.___gemShopInfo.shopType || false;
    },
    isObserve() {
      return this.pages[this.pageType].list.length > 0 && !this.pages[this.pageType].limited;
    }
  },
  created() {
    this.fetch();
  },
  mounted() {
    try {
      trackEvent({
        eventId: PRICING_MODEL_EVENT.VIEW_UNPUBLISH_POP_UP,
        properties: {
          type: this.pageType
        }
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  },
  methods: {
    ...mapActions("account", {
      whoAmI: "accountWhoAmI"
    }),
    ...mapActions("page", {
      fetchAllItems: "fetchAllItems",
      fetchAllAssignItems: "fetchAllAssignItems"
    }),
    ...mapActions("downgrade", {
      getPublishedPage: "getPublishedPage",
      unpublishPage: "unpublishPage",
      getPublishPageCount: "getPublishPageCount"
    }),
    ...mapMutations("downgrade", {
      setSelectAll: "SET_SELECT_ALL",
      resetSelect: "RESET_SELECT_PAGE",
      mutateStoreQuery: "PAGE_UPDATE_QUERY",
      closeUnpublishModal: "CLOSE_UNPUBLISH_MODAL"
    }),
    cancel() {
      if (this.isSubmitting) return;
      this.closeUnpublishModal();
    },
    async fetch() {
      if (this.listPages.length > 0) return;
      this.loadingIndicator = true;
      await this.getPublishedPage({
        type: this.pageType
      })
        .catch(err => {
          // eslint-disable-next-line no-console
          console.error(err);
        })
        .finally(() => {
          this.loadingIndicator = false;
        });
      if (this.pageType == "productpages") {
        this.fetchAllItems({
          shop: this.shopInfo.shopifyDomain,
          type: this.pageType
        });
        this.fetchAllAssignItems({
          shop: this.shopInfo.shopifyDomain,
          type: this.pageType
        });
      }
    },
    isPublishScheduled(page) {
      if (!page.events) return false;
      return page.events.some(item => item.event == "schedule_publish");
    },
    isRunningAbTesting(page) {
      return page.runningCampaignId > 0;
    },
    isDefault(page) {
      if (page.isTemplateV7) {
        return page.default;
      }
      if (["template-collection", "template-index", "template-product"].includes(page.type)) {
        return page.published ? true : false;
      }
    },
    selectAll() {
      if (this.selectedPage > 0 && !this.isSelectAll()) {
        this.resetSelect(this.pageType);
      } else {
        this.setSelectAll({
          pageType: this.pageType,
          pageList: this.filteredPages
        });
      }
    },
    queryPages({ value }) {
      this.indicatingSearchPage = true;
      if (!value || !value.trim()) {
        this.indicatingSearchPage = false;
      }
      this.runnerSearch && window.clearTimeout(this.runnerSearch);
      this.runnerSearch = window.setTimeout(() => {
        this.mutateStoreQuery({
          type: this.pageType,
          value: value
        });
        this.indicatingSearchPage = false;
      }, 200);
    },
    isSelectAll() {
      return this.filteredPages.every(item => this.unpublishList.includes(item.id));
    },
    gotItUnpublishPage() {
      localStorage.setItem("gotUnpublishMessageList", true);
    },
    postUnpublishActions() {
      this.getPublished();
      this.whoAmI({ isLoading: false });
      this.gSnackbarCommit({
        message: this.$t("status.success"),
        type: "success"
      });
      this.isSubmitting = false;
      this.queryPage = "";
    },
    async getPublished() {
      this.loadingIndicator = true;
      await this.getPublishedPage({
        type: this.pageType,
        refresh: true
      }).then(res => {
        if (res.pages && res.pages.length == 0) {
          this.gSnackbarCommit({ message: this.$t("status.success"), type: "success" });
          this.whoAmI({ isLoading: false });
          return this.cancel();
        }
      }).finally(() => this.loadingIndicator = false);
    },
    initUnpublishSocketProcess(totalSteps) {
      let currentStep = 0;
      const socketData = {
        INTERVAL: 10,
        percentage: 0
      };
      window._webSocket.onmessage("page_unpublish_processing", () => {
        currentStep++;
        const percentTo = Math.round((100 * currentStep) / totalSteps);

        let percent = socketData.percentage;
        if (percent < 0) {
          percent = 0;
        }
        let progressInterval = setInterval(() => {
          percent++;
          document.getElementsByClassName("unpublish__footer_btn__progress-bar")[0].style.width = percent + "%";
          if (percent > percentTo) {
            socketData.percentage = percentTo;
            if (totalSteps == currentStep) {
              document.getElementsByClassName("unpublish__footer_btn__progress-bar")[0].style.width = "0%";
              window._webSocket.offmessage("page_unpublish_processing");
              this.postUnpublishActions();
            }
            clearInterval(progressInterval);
          }
        }, socketData.INTERVAL);
      });
    },
    async unpublish() {
      if (this.selectedPage == 0) return;
      try {
        this.isSubmitting = true;
        this.gSnackbarCommit({
          message: this.$t("status.processing"),
          type: "loading"
        });
        await this.unpublishPage(this.pageType)
          .then(res => {
            this.handleUnpublishResponse(res)
          })
          .catch(error => {
            const message = error?.response?.data?.message || "Error";
            this.gSnackbarCommit({
              message: this.getErrorMessage(message),
              type: "error"
            });
          });
      } catch (error) {
        const message = error?.response?.data?.message || "Error";
        this.gSnackbarCommit({
          message: this.getErrorMessage(message),
          type: "error"
        });
      }
    },
    handleUnpublishResponseV6(res) {
      if (!window._webSocket || window._webSocket.connection.readyState !== 1) {
        return this.postUnpublishActions();
      }
      this.initUnpublishSocketProcess(res.totalSteps);
    },
    handleUnpublishResponse(res) {
      if (this.shopVersion.isGemPagesV7) {
        this.postUnpublishActions();
        this.getPublishPageCount();
        return;
      }
      if (this.shopVersion.isGemPagesV6) {
        this.handleUnpublishResponseV6(res);
        return;
      }
      if (this.shopVersion.isGemPagesMixed) {
        if (res.v7) {
          this.getPublishPageCount();
          if (res.v6) return this.postUnpublishActions();
          this.postUnpublishActions();
        }
  
        if (res.v6)
          this.handleUnpublishResponseV6(res.v6)
      }
    }
  }
};
</script>

<style lang="scss">
.unpublish {
  &__modal {
    width: 600px !important;
  }
  &__loader {
    position: relative;
    min-height: calc(100% - 55px - 64px);
  }
  &__search {
    position: sticky;
    z-index: 3;
    top: 0;
    left: 0;
    padding: 16px;
    background-color: $light2;
  }
  &__header {
    height: 100%;
    height: 55px;
    border-radius: 3px 3px 0px 0px;
  }
  &__body {
    position: relative;
    height: 563px;
    background-color: $light2;
  }
  &__footer {
    height: 64px;
    padding: 12px 16px;
    border-radius: 0px 0px 3px 3px;
    background-color: #ffffff;
    border-top: solid 1px $light3;
    &_btn {
      position: relative;
      height: 100%;
      // background-color: red;

      .opacity-0 {
        opacity: 0;
      }

      &.disabled {
        color: $textLightModeDisabled !important;
        background: $light2 !important;
      }

      &__number {
        position: absolute;
        z-index: 1;
      }

      &__progress-bar {
        border-radius: 3px;
        background: $accentRed400;
        width: 0%;
        height: 100%;
        position: absolute;
        left: 0;
      }
    }
  }
  &__select-all-checkbox {
    margin-right: 19px;
  }
  &__page-count {
    color: $accentRed300;
  }
}

.gfLoader-3Dots {
  font-size: 16px !important;
  font-weight: normal;
  text-align: center;
  margin-top: 70px;
  margin-left: 2px !important;
  color: #ffffff;
  letter-spacing: 1px;
  text-align: center;
  display: block;
  width: 100%;
}

.gfLoader-3Dots-dot1 {
  -webkit-animation: gfLoader-3Dots-dot1 1s infinite;
  animation: gfLoader-3Dots-dot1 1s infinite;
  letter-spacing: -1.5px;
  font-weight: bold;
}

.gfLoader-3Dots-dot2 {
  -webkit-animation: gfLoader-3Dots-dot2 1s infinite;
  animation: gfLoader-3Dots-dot2 1s infinite;
  letter-spacing: -1.5px;
  font-weight: bold;
}

.gfLoader-3Dots-dot3 {
  -webkit-animation: gfLoader-3Dots-dot3 1s infinite;
  animation: gfLoader-3Dots-dot3 1s infinite;
  letter-spacing: -1.5px;
  font-weight: bold;
}

@-webkit-keyframes gfLoader-3Dots-dot1 {
  0% {
    opacity: 0;
  }

  25% {
    opacity: 1;
  }

  50% {
    opacity: 1;
  }

  75% {
    opacity: 1;
  }

  100% {
    opacity: 0;
  }
}

@keyframes gfLoader-3Dots-dot1 {
  100%,
  0% {
    opacity: 0;
  }

  25% {
    opacity: 1;
  }

  50% {
    opacity: 1;
  }

  75% {
    opacity: 1;
  }
}

@-webkit-keyframes gfLoader-3Dots-dot2 {
  100%,
  0% {
    opacity: 0;
  }

  25% {
    opacity: 0;
  }

  50% {
    opacity: 1;
  }

  75% {
    opacity: 1;
  }
}

@keyframes gfLoader-3Dots-dot2 {
  100%,
  0% {
    opacity: 0;
  }

  25% {
    opacity: 0;
  }

  50% {
    opacity: 1;
  }

  75% {
    opacity: 1;
  }
}

@-webkit-keyframes gfLoader-3Dots-dot3 {
  100%,
  0% {
    opacity: 0;
  }

  25% {
    opacity: 0;
  }

  50% {
    opacity: 0;
  }

  75% {
    opacity: 1;
  }
}

@keyframes gfLoader-3Dots-dot3 {
  100%,
  0% {
    opacity: 0;
  }

  25% {
    opacity: 0;
  }

  50% {
    opacity: 0;
  }

  75% {
    opacity: 1;
  }
}
</style>
